// File: src/pages/ConfirmSiteReplace.jsx
// 
// This page is shown when a user tries to connect a new site (BigCommerce/Shopify)
// but already has an existing site connected. They must confirm to replace it.

import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Icon,
  Spinner,
  Box,
} from '@shopify/polaris';
import { 
  AlertCircleIcon, 
  ArrowRightIcon 
} from '@shopify/polaris-icons';
import { AuthContext } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

// Define custom colors for use in style attributes
const ACCENT_COLOR = '#00A8A9';
const PRIMARY_BUTTON_COLOR = '#6A0DAD';
// A slightly darker version of the primary color for a hover effect
const PRIMARY_BUTTON_HOVER_COLOR = '#5A0D9D'; 

export default function ConfirmSiteReplace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, setToken } = useContext(AuthContext);
  
  // Get params from URL
  const pendingToken = searchParams.get('pending_token');
  const urlToken = searchParams.get('token');
  const oldSiteName = searchParams.get('old_site_name') || 'Your current site';
  const oldSitePlatform = searchParams.get('old_site_platform') || 'Unknown';
  const newSiteName = searchParams.get('new_site_name') || 'New site';
  const newPlatform = searchParams.get('new_platform') || 'BigCommerce';
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingDetails, setPendingDetails] = useState(null);
  const [fetchingDetails, setFetchingDetails] = useState(true);
  
  // LOGIC BLOCK 1: Capture token from URL if present (NO LOGIC CHANGE)
  useEffect(() => {
    if (urlToken && !token) {
      setToken(urlToken);
      localStorage.setItem('token', urlToken);
    }
  }, [urlToken, token, setToken]);
  
  // LOGIC BLOCK 2: Fetch pending replacement details (NO LOGIC CHANGE)
  useEffect(() => {
    if (!pendingToken || !token) {
      setFetchingDetails(false);
      return;
    }
    
    const fetchDetails = async () => {
      try {
        const res = await fetch(
          `${API}/api/sites/pending-replacement?pending_token=${pendingToken}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (res.ok) {
          const data = await res.json();
          setPendingDetails(data);
        } else {
          // If not found, the pending replacement may have expired
          setError('This replacement request has expired or is invalid.');
        }
      } catch (err) {
        console.error('Failed to fetch pending details:', err);
      } finally {
        setFetchingDetails(false);
      }
    };
    
    fetchDetails();
  }, [pendingToken, token]);
  
  // LOGIC BLOCK 3: Confirm handler (NO LOGIC CHANGE)
  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/sites/confirm-replace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pending_token: pendingToken,
          confirm: true,
        }),
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to replace site');
      }
      
      const data = await res.json();
      
      // Redirect to the new site's integrations page
      if (data.redirect_to) {
        navigate(data.redirect_to);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err?.message || String(err) || 'Failed to replace site');
      setLoading(false);
    }
  };
  
  // LOGIC BLOCK 4: Cancel handler (NO LOGIC CHANGE)
  const handleCancel = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/sites/confirm-replace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pending_token: pendingToken,
          confirm: false,
        }),
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to cancel');
      }
      
      // Go back to settings
      navigate('/settings?tab=sites');
    } catch (err) {
      setError(err?.message || String(err) || 'Failed to cancel');
      setLoading(false);
    }
  };
  
  // ERROR STATE: Invalid Request (NO LOGIC CHANGE)
  if (!pendingToken) {
    return (
      <Page title="Invalid Request">
        <Layout>
          <Layout.Section>
            <Banner status="critical">
              <Text as="p">Missing pending token. Please try connecting your site again.</Text>
            </Banner>
            <Box paddingBlockStart="400">
              <Button onClick={() => navigate('/settings?tab=sites')}>
                Go to Settings
              </Button>
            </Box>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }
  
  // LOADING STATE (NO LOGIC CHANGE)
  if (fetchingDetails) {
    return (
      <Page title="Loading...">
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <BlockStack alignment="center" gap="300">
                <Spinner size="large" />
                <Text as="p">Loading replacement details...</Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }
  
  // DATA PREP (NO LOGIC CHANGE)
  const oldSite = pendingDetails?.old_site || {
    name: oldSiteName,
    platform: oldSitePlatform,
  };
  const newSite = pendingDetails?.new_site || {
    name: newSiteName,
    platform: newPlatform,
  };
  
  // MAIN RENDER (VISUAL IMPROVEMENTS)
  return (
    <Page
      title="Replace Connected Site?"
      // Use the destructive tone on the back action to match the severity
      backAction={{ content: 'Cancel & Keep Current Site', onAction: handleCancel }}
    >
      <Layout>
        {/* Use a smaller section to focus the user's attention */}
        <Layout.Section secondary>
          <Card sectioned>
            <BlockStack gap="400">
              
              {error && (
                <Banner status="critical" onDismiss={() => setError(null)}>
                  <p>{typeof error === 'string' ? error : error.message || 'An error occurred'}</p>
                </Banner>
              )}
              
              {/* Main Warning Banner */}
              <Banner status="warning" title="Account Site Limit Reached">
                <Text as="p">
                  BaoBrain only allows one site per account. To connect your new {newSite.platform} store, 
                  you must replace your current site connection.
                </Text>
              </Banner>
              
              <Text as="h2" variant="headingLg">Confirm Replacement</Text>
              
              {/* Comparison Card Container */}
              <InlineStack alignment="center" gap="400" wrap={false}>
                  
                  {/* 1. Current Site (To be removed) */}
                  <Card padding="400" background="bg-surface-critical" tone="critical">
                      <BlockStack gap="200">
                          <Text as="h3" variant="headingMd">
                              Currently Connected
                          </Text>
                          <Text as="p" tone="critical" fontWeight="bold">
                              Will be permanently removed:
                          </Text>
                          <Box paddingBlockStart="100">
                              <BlockStack gap="50">
                                  <Text as="p" variant="bodyLg" fontWeight="semibold">
                                      {oldSite.name}
                                  </Text>
                                  <Text as="p" color="subdued">
                                      Platform: {oldSite.platform}
                                  </Text>
                              </BlockStack>
                          </Box>
                      </BlockStack>
                  </Card>
                  
                  {/* Arrow Separator */}
                  <Box paddingBlock="200">
                      <Icon source={ArrowRightIcon} size="large" tone="subdued" />
                  </Box>
                  
                  {/* 2. New Site (To be connected) */}
                  {/* Styled to visually match the critical card structure using the ACCENT_COLOR */}
                  <Card 
                      padding="400" 
                      // Custom styles for border and background
                      style={{ 
                          border: `2px solid ${ACCENT_COLOR}`, 
                          backgroundColor: 'var(--p-color-bg-subdued)', // Lightest non-white background
                      }}
                  >
                      <BlockStack gap="200">
                          <Text as="h3" variant="headingMd" style={{ color: ACCENT_COLOR }}>
                              New Connection
                          </Text>
                          <Text as="p" fontWeight="bold" style={{ color: ACCENT_COLOR }}>
                              Will be activated:
                          </Text>
                          <Box paddingBlockStart="100">
                              <BlockStack gap="50">
                                  <Text as="p" variant="bodyLg" fontWeight="semibold">
                                      {newSite.name}
                                  </Text>
                                  <Text as="p" color="subdued">
                                      Platform: {newSite.platform}
                                  </Text>
                              </BlockStack>
                          </Box>
                      </BlockStack>
                  </Card>
              </InlineStack>

              {/* Warning about data loss - using a list stack for better Polaris compliance */}
              <Banner status="info" icon={<Icon source={AlertCircleIcon} />}>
                <BlockStack gap="200">
                  <Text as="p" fontWeight="bold">
                    Important: Replacing your site will:
                  </Text>
                  <Box paddingInlineStart="400">
                    <BlockStack gap="100">
                      <Text as="li">Remove tracking from your current site.</Text>
                      <Text as="li">Delete historical data associated with the old site.</Text>
                      <Text as="li">Set up fresh tracking on your new {newSite.platform} store.</Text>
                    </BlockStack>
                  </Box>
                </BlockStack>
              </Banner>
              
              {/* Action Buttons */}
              <InlineStack gap="300" alignment="center">
                <Button
                  onClick={handleCancel}
                  disabled={loading}
                  size="large"
                >
                  Keep Current Site
                </Button>
                <Button
                  onClick={handleConfirm}
                  loading={loading}
                  size="large"
                  // Apply custom theme colors and hover state
                  style={{ 
                      backgroundColor: PRIMARY_BUTTON_COLOR, 
                      color: 'white',
                      border: 'none', 
                      cursor: 'pointer',
                      // Ensure the button acts like a primary button when not destructive
                      boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = PRIMARY_BUTTON_HOVER_COLOR;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = PRIMARY_BUTTON_COLOR;
                  }}
                >
                  Confirm & Replace Site
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}